import React, { useEffect, useState } from 'react'
import StyledCollapsibleCard from '../../Common/StyledCollapsibleCard'
import StyledChip from '../../Common/StyledChip'
import { Card, Typography, makeStyles, Button, CircularProgress } from '@material-ui/core'
import { useDispatch } from 'react-redux'
import { useTheme } from '@material-ui/core/styles'
import { api } from '../../../services/api'
import EditNoteDialog from '../../Dialogs/Visit/EditNoteDialog'
import { gray, purple } from '../../../utils/colors'
import NotesTable from '../../StaffNotes/NotesTable'
import { getPetStaffNotes } from '../../../actions/pets/pets.actions'
import { AUTOMATED_ALERTS, STAFF_NOTE_STATUS, STAFF_NOTE_TYPES } from '../../../utils'
import { StyledSimpleFilter } from '../../Common/StyledSimpleFilter'

const useStyles = makeStyles((theme) => ({
	card: {
		backgroundColor: gray[100],
		boxShadow: 'none',
		border: `1px solid ${gray[400]}`,
		borderWidth: 1,
		padding: '1rem'
	},
	contentContainer: { display: 'flex', justifyContent: 'space-between', marginBottom: '.75rem' }
}))

const PetStaffNotesCard = (props) => {
	let { pet } = props
	const theme = useTheme()
	const classes = useStyles(theme)
	const dispatch = useDispatch()

	const [staffNoteCounts, setStaffNoteCounts] = useState([])
	const [loading, setLoading] = useState(false)
	const [staffNote, setStaffNote] = useState(null)
	const [dialogShown, setDialogShown] = useState(null)
	const [staffNotes, setStaffNotes] = useState(null)
	const [filteredStaffNotes, setFilteredStaffNotes] = useState([])
	const [selectedFilters, setSelectedFilters] = useState([])

	useEffect(() => {
		if (pet?.staff_notes_counts) {
			setStaffNoteCounts(pet.staff_notes_counts)
		}
		if (pet?.staff_notes) {
			setStaffNotes(pet.staff_notes)
			if (selectedFilters.length > 0) {
				const filtered = pet.staff_notes.filter((note) =>
					selectedFilters.includes(note.filter_type || note.type)
				)
				setFilteredStaffNotes(filtered)
			} else {
				setFilteredStaffNotes(pet.staff_notes)
			}
		}
	}, [pet])

	const handleCardExpanded = async () => {
		if (!staffNotes) {
			try {
				setLoading(true)
				await getPetStaffNotes(dispatch, pet._id)
			} catch (e) {
				console.log('PetStaffNotesCard > error getting staff notes', e)
			} finally {
				setLoading(false)
			}
		}
	}

	const handleSetIsShown = () => {
		setStaffNote(null)
		setDialogShown(false)
	}

	const saveNote = async (n) => {
		try {
			const { data } = await api.put(`/updateStaffNote`, n)
			let updatedNotes = []
			if (!n._id) {
				updatedNotes = staffNotes ? [data, ...staffNotes] : [data]
			} else {
				updatedNotes = staffNotes?.map((note) => {
					if (note._id !== n._id) {
						return note
					}
					return data
				}) || [data]
			}

			const newStaffNoteCountMap = {
				[STAFF_NOTE_STATUS.InProgress]: 0,
				[STAFF_NOTE_STATUS.Open]: 0
			}

			updatedNotes.forEach(({ status }) => {
				if (status !== STAFF_NOTE_STATUS.Closed) {
					let stat = status || STAFF_NOTE_STATUS.Open
					if (newStaffNoteCountMap[stat]) {
						newStaffNoteCountMap[stat] = newStaffNoteCountMap[stat] + 1
					} else {
						newStaffNoteCountMap[stat] = 1
					}
				}
			})

			const staff_notes_counts = []

			for (const entry in newStaffNoteCountMap) {
				if (newStaffNoteCountMap[entry] > 0) {
					staff_notes_counts.push({ _id: entry, count: newStaffNoteCountMap[entry] })
				}
			}

			dispatch({
				type: 'UPDATE_PET_STAFF_NOTES',
				payload: { _id: pet._id, staff_notes: updatedNotes, staff_notes_counts }
			})

			setStaffNote(null)
			setDialogShown(false)
		} catch (e) {
			console.log('error...', e)
		} finally {
			setLoading(false)
		}
	}

	const handleCreateStaffNote = () => {
		setDialogShown(true)
		setStaffNote({
			pet_id: pet._id,
			status: STAFF_NOTE_STATUS.Open,
			tags: [],
			description: ''
		})
	}

	const handleViewEditStaffNote = (note) => {
		setDialogShown(true)
		setStaffNote({ ...note })
	}

	const handleFiltersChanged = (filters) => {
		setSelectedFilters(filters)
	}

	useEffect(() => {
		if (selectedFilters.length > 0) {
			const filtered = staffNotes.filter((note) => selectedFilters.includes(note.filter_type || note.type))
			setFilteredStaffNotes(filtered)
		} else {
			setFilteredStaffNotes(staffNotes)
		}
	}, [selectedFilters])

	return (
		<StyledCollapsibleCard
			onExpanded={handleCardExpanded}
			title={
				<div style={{ display: 'flex', gap: '1rem' }}>
					<span>Staff Notes</span>
					{staffNoteCounts.map(({ _id: statusName, count }) => {
						const skiptownvariant =
							statusName === STAFF_NOTE_STATUS.Open
								? 'yellow'
								: statusName === STAFF_NOTE_STATUS.InProgress
								? 'purple'
								: 'gray'
						return (
							<StyledChip
								skiptownvariant={skiptownvariant}
								label={
									<div>
										<span style={{ fontWeight: '800' }}>{count}</span>
										<span> {statusName}</span>
									</div>
								}></StyledChip>
						)
					})}
				</div>
			}>
			<div className={classes.contentContainer}>
				<StyledSimpleFilter
					filters={[...Object.values(STAFF_NOTE_TYPES), AUTOMATED_ALERTS]}
					onFiltersChanged={handleFiltersChanged}
				/>
				<Button onClick={handleCreateStaffNote} style={{ padding: 0 }} disabled={loading}>
					<Typography
						style={{
							color: purple[500],
							fontSize: 16,
							textTransform: 'capitalize'
						}}>
						Add Note
					</Typography>
				</Button>
			</div>
			<Card className={classes.card}>
				{loading ? (
					<div>
						<CircularProgress />
					</div>
				) : null}
				<NotesTable
					staff_notes={filteredStaffNotes}
					setStaffNote={handleViewEditStaffNote}
					loading={loading}
				/>
			</Card>
			<EditNoteDialog
				isShown={dialogShown}
				setIsShown={handleSetIsShown}
				pet={pet}
				note={staffNote}
				visit={staffNote?.visit || null}
				saveNote={saveNote}
				loading={loading}
			/>
		</StyledCollapsibleCard>
	)
}

export default PetStaffNotesCard
